type RealtimeLogLevel = 'info' | 'warn' | 'error'
type RealtimeLogArgs = unknown[]
type RealtimeLogManager = WechatMiniprogram.RealtimeLogManager

let realtimeLogManager: RealtimeLogManager | null | undefined

// 日志能力必须是旁路能力，任何异常都不能向业务调用方透出。
function safelyRun(task: () => void) {
  try {
    task()
  }
  catch {
    // Logging must never interrupt business logic.
  }
}

// 延迟获取并缓存日志实例，非微信环境或获取失败时降级为 null。
function getRealtimeLogManager() {
  if (realtimeLogManager !== undefined) {
    return realtimeLogManager
  }

  try {
    if (typeof wx === 'undefined' || typeof wx.getRealtimeLogManager !== 'function') {
      realtimeLogManager = null
      return realtimeLogManager
    }

    realtimeLogManager = wx.getRealtimeLogManager()
  }
  catch {
    realtimeLogManager = null
  }

  return realtimeLogManager
}

// 统一写入入口，避免各日志级别重复处理可用性判断和异常兜底。
function writeRealtimeLog(level: RealtimeLogLevel, args: RealtimeLogArgs) {
  safelyRun(() => {
    const logger = getRealtimeLogManager()
    const logMethod = logger?.[level]

    if (typeof logMethod !== 'function') {
      return
    }

    logMethod.apply(logger, args)
  })
}

/**
 * 写入 info 级别实时日志。
 */
export function info(...args: RealtimeLogArgs) {
  writeRealtimeLog('info', args)
}

/**
 * 写入 warn 级别实时日志。
 */
export function warn(...args: RealtimeLogArgs) {
  writeRealtimeLog('warn', args)
}

/**
 * 写入 error 级别实时日志。
 */
export function error(...args: RealtimeLogArgs) {
  writeRealtimeLog('error', args)
}

/**
 * 设置实时日志过滤关键字。
 */
export function setFilterMsg(msg: string) {
  safelyRun(() => {
    const logger = getRealtimeLogManager()

    if (!logger || typeof logger.setFilterMsg !== 'function') {
      return
    }

    logger.setFilterMsg(msg)
  })
}

/**
 * 追加实时日志过滤关键字。
 */
export function addFilterMsg(msg: string) {
  safelyRun(() => {
    const logger = getRealtimeLogManager()

    if (!logger || typeof logger.addFilterMsg !== 'function') {
      return
    }

    logger.addFilterMsg(msg)
  })
}

export const realtimeLog = {
  info,
  warn,
  error,
  setFilterMsg,
  addFilterMsg,
}

export default realtimeLog
